/**
 * @voltxampere/web — `<vxa-import-dialog>` (Sprint 16)
 *
 * Modal SPICE netlist importer.
 *
 * UX:
 *   1. Open via Cmd+I or the "Import" topbar button.
 *   2. Paste a SPICE netlist into the textarea (or click a sample).
 *   3. "Preview" parses the input and shows component / net counts.
 *   4. "Apply" commits the parsed circuit via `LoadCircuitCommand`
 *      (single Cmd+Z reverts).
 *   5. Esc / "Cancel" closes without applying.
 *
 * Sprint 14 D5 discipline: every action is wired through
 * `data-action` + a parent `@click` listener so happy-dom-driven
 * tests work without the Lit `@click` directive ceremony.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import {
  importDialogOpen,
  closeImportDialog,
  parseSpiceText,
  commitImport,
  type ImportPreview,
} from "../store/import-export.js";
import { SPICE_SAMPLES, type SpiceSampleKey } from "../fixtures/spice-samples.js";
import { t } from "../i18n/index.js";
import { SignalController } from "../lit-utils/signal-controller.js";

@customElement("vxa-import-dialog")
export class VxaImportDialog extends LitElement {
  private readonly _open = new SignalController(this, importDialogOpen);

  @state() private accessor inputText = "";
  @state() private accessor preview: ImportPreview | null = null;

  static override styles = css`
    :host {
      position: fixed;
      inset: 0;
      z-index: 100;
      display: none;
    }

    :host([data-open]) {
      display: grid;
      place-items: center;
      background: rgba(7, 7, 9, 0.78);
      backdrop-filter: blur(4px);
    }

    .panel {
      width: min(720px, 92vw);
      max-height: 86vh;
      background: var(--ink);
      border: 1px solid var(--brass-faint);
      border-radius: 12px;
      box-shadow:
        0 24px 56px rgba(0, 0, 0, 0.55),
        0 0 0 1px var(--brass-faint);
      display: grid;
      grid-template-rows: auto 1fr auto;
      overflow: hidden;
    }

    .header {
      padding: var(--s-5) var(--s-6);
      border-bottom: 1px solid var(--ivory-wisp);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .title {
      font-family: var(--font-display);
      font-size: 22px;
      letter-spacing: -0.01em;
      color: var(--ivory);
    }

    .close {
      background: none;
      border: 0;
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      color: var(--ivory-dim);
      cursor: pointer;
      padding: 6px 12px;
    }

    .close:hover {
      color: var(--brass);
    }

    .body {
      padding: var(--s-5) var(--s-6);
      display: grid;
      gap: var(--s-4);
      overflow-y: auto;
    }

    .section-label {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.18em;
      color: var(--ivory-dim);
      text-transform: uppercase;
    }

    .samples {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .sample {
      font-family: var(--font-mono);
      font-size: 11px;
      letter-spacing: 0.05em;
      padding: 4px 10px;
      border: 1px solid var(--brass-faint);
      background: transparent;
      color: var(--ivory-dim);
      cursor: pointer;
      border-radius: 4px;
    }

    .sample:hover {
      color: var(--brass);
      border-color: var(--brass);
    }

    textarea {
      width: 100%;
      min-height: 220px;
      max-height: 340px;
      font-family: var(--font-mono);
      font-size: 13px;
      line-height: 1.5;
      color: var(--ivory);
      background: var(--void);
      border: 1px solid var(--ivory-wisp);
      border-left: 3px solid var(--brass-faint);
      padding: 12px 14px;
      resize: vertical;
      box-sizing: border-box;
    }

    textarea:focus {
      outline: none;
      border-color: var(--brass);
    }

    .preview-pane {
      padding: var(--s-3) var(--s-4);
      border: 1px solid var(--ivory-wisp);
      background: rgba(255, 255, 255, 0.02);
      font-family: var(--font-mono);
      font-size: 12px;
      color: var(--ivory-dim);
      display: grid;
      gap: 4px;
      max-height: 140px;
      overflow-y: auto;
    }

    .preview-summary {
      color: var(--ivory);
      letter-spacing: 0.04em;
    }

    .preview-warning {
      color: var(--amber, #d4a857);
    }

    .preview-error {
      color: var(--rust, #c0644a);
    }

    .footer {
      padding: var(--s-4) var(--s-6);
      border-top: 1px solid var(--ivory-wisp);
      display: flex;
      gap: var(--s-3);
      justify-content: flex-end;
    }

    button.action {
      font-family: var(--font-mono);
      font-size: var(--fs-mono-tiny);
      letter-spacing: 0.22em;
      padding: 8px 18px;
      border: 1px solid var(--brass-faint);
      background: transparent;
      color: var(--ivory-dim);
      cursor: pointer;
      transition:
        border-color var(--transition-hover),
        background var(--transition-hover),
        color var(--transition-hover);
    }

    button.action:hover {
      color: var(--brass);
      border-color: var(--brass);
    }

    button.action.primary {
      color: var(--brass);
      border-color: var(--brass);
      background: var(--brass-ghost);
    }

    button.action.primary:hover {
      color: var(--brass-hot);
      background: rgba(196, 145, 89, 0.18);
    }

    button.action[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `;

  override updated(): void {
    this.toggleAttribute("data-open", this._open.value);
  }

  override render() {
    if (!this._open.value) return nothing;
    return html`
      <div class="panel" @click=${this.handleAction}>
        <div class="header">
          <div class="title">${t("import.title")}</div>
          <button
            class="close"
            data-action="cancel"
            type="button"
            aria-label=${t("modal.close")}
            title=${t("modal.close") + " (Esc)"}
          >
            ${t("modal.close")} <span aria-hidden="true">✕</span>
          </button>
        </div>
        <div class="body">
          <div>
            <div class="section-label">${t("import.samples.label")}</div>
            <div class="samples">${this.renderSamples()}</div>
          </div>
          <div>
            <div class="section-label">${t("import.paste.label")}</div>
            <textarea
              .value=${this.inputText}
              @input=${this.handleInput}
              placeholder=${t("import.paste.placeholder")}
              spellcheck="false"
            ></textarea>
          </div>
          ${this.renderPreview()}
        </div>
        <div class="footer">
          <button class="action" data-action="cancel" type="button">
            ${t("import.button.cancel")}
          </button>
          <button class="action" data-action="preview" type="button">
            ${t("import.button.preview")}
          </button>
          <button
            class="action primary"
            data-action="commit"
            type="button"
            ?disabled=${!this.preview?.ok}
          >
            ${t("import.button.apply")}
          </button>
        </div>
      </div>
    `;
  }

  private renderSamples() {
    const keys = Object.keys(SPICE_SAMPLES) as SpiceSampleKey[];
    return keys.map(
      (k) => html`
        <button class="sample" data-action="load-sample" data-sample=${k} type="button">
          ${k}
        </button>
      `,
    );
  }

  private renderPreview() {
    if (this.preview === null) return nothing;
    if (!this.preview.ok) {
      return html`
        <div class="preview-pane">
          ${this.preview.errors.map(
            (e) =>
              html`<div class="preview-error">
                ${t("import.error.line", {
                  line: String(e.line),
                  col: String(e.col),
                  message: e.message,
                })}
              </div>`,
          )}
        </div>
      `;
    }
    const summary = t("import.preview.summary", {
      components: String(this.preview.circuit?.components.length ?? 0),
      nets: String(this.preview.circuit?.nets.length ?? 0),
      warnings: String(this.preview.warnings.length),
    });
    return html`
      <div class="preview-pane">
        <div class="preview-summary">${summary}</div>
        ${this.preview.warnings.map((w) => html`<div class="preview-warning">${w}</div>`)}
      </div>
    `;
  }

  private handleInput = (e: Event): void => {
    const target = e.target as HTMLTextAreaElement;
    this.inputText = target.value;
    this.preview = null;
  };

  private handleAction = (e: Event): void => {
    const path = e.composedPath();
    const target = path.find(
      (n): n is HTMLElement => n instanceof HTMLElement && typeof n.dataset.action === "string",
    );
    if (!target) return;
    const action = target.dataset.action;
    switch (action) {
      case "cancel":
        this.cancel();
        break;
      case "preview":
        this.runPreview();
        break;
      case "commit":
        this.commit();
        break;
      case "load-sample":
        this.loadSample(target.dataset.sample as SpiceSampleKey | undefined);
        break;
      default:
        break;
    }
  };

  private cancel(): void {
    this.inputText = "";
    this.preview = null;
    closeImportDialog();
  }

  private runPreview(): void {
    this.preview = parseSpiceText(this.inputText);
  }

  private commit(): void {
    this.preview ??= parseSpiceText(this.inputText);
    if (commitImport(this.preview)) {
      this.cancel();
    }
  }

  private loadSample(key: SpiceSampleKey | undefined): void {
    if (!key) return;
    const sample = SPICE_SAMPLES[key];
    this.inputText = sample;
    this.preview = parseSpiceText(sample);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-import-dialog": VxaImportDialog;
  }
}
